import Grid from "@mui/material/Grid";
import MDBox from "components/MDBox";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Footer from "examples/Footer";
import ReportsBarChart from "examples/Charts/BarCharts/ReportsBarChart";
import ReportsLineChart from "examples/Charts/LineCharts/ReportsLineChart";
import ComplexStatisticsCard from "examples/Cards/StatisticsCards/ComplexStatisticsCard";

// Data services
import getIngresosPorEmpresa from "layouts/dashboard/data/reportsBarChartData";
import {
  getAccionesChart,
  getIngresosConsolidadosChart,
} from "layouts/dashboard/data/reportsLineChartData";

import {
  getCount,
  getSucursales,
  getIngresos,
  getActivos,
} from "layouts/dashboard/data/dataComplexStatisticsCard";
import React, { useState, useEffect } from "react";
import { useParams } from "react-router-dom"; // Importa useParams
import { useMaterialUIController } from "context";
import Projects from "layouts/dashboard/components/Projects";
import OrdersOverview from "layouts/dashboard/components/OrdersOverview";

/**
 * Componente principal del Dashboard.
 * Muestra estadísticas, gráficas y componentes adicionales.
 */
function Dashboard() {
  const { id } = useParams(); // Obtiene el ID de la empresa de la URL
  const [controller] = useMaterialUIController();
  const { selectedCompanyId: globalId } = controller;
  const effectiveId = id || globalId; // Prioriza el de la URL, luego el global

  // Estados para los datos principales
  const [count, setCount] = useState(null);
  const [ingresos, setIngresos] = useState(null);
  const [sucursales, setSucursales] = useState(null);
  const [activos, setActivos] = useState(null);

  // Estados para las gráficas
  const [ingresosPorEmpresaData, setIngresosPorEmpresaData] = useState({
    labels: [],
    datasets: [{ label: "Ingresos", data: [] }],
  });
  const [accionesData, setAccionesData] = useState({
    sales: {
      labels: [],
      datasets: [{ label: "Ventas (Cantidad)", data: [] }],
    },
  });
  const [ingresosConsolidadosData, setIngresosConsolidadosData] = useState({
    tasks: {
      labels: [],
      datasets: [{ label: "Ingresos ($)", data: [] }],
    },
  });

  // Efecto para cargar los datos al montar el componente
  useEffect(() => {
    const fetchData = async () => {
      try {
        const [
          countData,
          ingresosData,
          sucursalesData,
          activosData,
          ingresosEmpresaChart,
          accionesChart,
          ingresosConsolidadosChart,
        ] = await Promise.all([
          getCount(effectiveId),
          getIngresos(effectiveId),
          getSucursales(effectiveId),
          getActivos(effectiveId),
          getIngresosPorEmpresa(effectiveId),
          getAccionesChart(effectiveId),
          getIngresosConsolidadosChart(effectiveId),
        ]);

        setCount(countData ?? "0");
        setIngresos(
          Array.isArray(ingresosData) ? [ingresosData[0], Number(ingresosData[1])] : ["$0.00", 0]
        );
        setSucursales(
          Array.isArray(sucursalesData) ? [sucursalesData[0], Number(sucursalesData[1])] : ["0", 0]
        );
        setActivos(
          Array.isArray(activosData) ? [activosData[0], Number(activosData[1])] : ["$0.00", 0]
        );

        // Actualizar estados de gráficas
        setIngresosPorEmpresaData(ingresosEmpresaChart);
        setAccionesData(accionesChart);
        setIngresosConsolidadosData(ingresosConsolidadosChart);
      } catch (error) {
        console.error("Error loading dashboard data:", error);
        setCount("0");
        setIngresos(["$0.00", 0]);
        setSucursales(["0", 0]);
        setActivos(["$0.00", 0]);
      }
    };

    fetchData();
  }, [effectiveId]);

  // Utilidad para validar y formatear el porcentaje
  const formatPercentage = (value) => {
    if (typeof value === "number" && !isNaN(value)) {
      const sign = value > 0 ? "+" : "";
      return `${sign}${value}%`;
    }
    return "";
  };

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox py={3}>
        {/* Tarjetas de estadísticas principales */}
        <Grid container spacing={3}>
          {/* Activos */}
          <Grid item xs={12} md={6} lg={3}>
            <MDBox mb={1.5}>
              <ComplexStatisticsCard
                color="success"
                icon="account_balance_wallet"
                title="Activos"
                count={activos === null ? "Cargando..." : activos[0]}
                percentage={
                  activos === null
                    ? { color: "secondary", amount: "Cargando...", label: "" }
                    : {
                      color:
                        typeof activos[1] === "number" && activos[1] >= 0 ? "success" : "error",
                      amount: formatPercentage(activos[1]),
                      label: "que la semana pasada",
                    }
                }
              />
            </MDBox>
          </Grid>
          {/* Ingresos */}
          <Grid item xs={12} md={6} lg={3}>
            <MDBox mb={1.5}>
              <ComplexStatisticsCard
                color="primary"
                icon="trending_up"
                title="Ingresos"
                count={ingresos === null ? "Cargando..." : ingresos[0]}
                percentage={
                  ingresos === null
                    ? { color: "secondary", amount: "Cargando...", label: "" }
                    : {
                      color:
                        typeof ingresos[1] === "number" && ingresos[1] >= 0 ? "success" : "error",
                      amount: formatPercentage(ingresos[1]),
                      label: "que el mes pasado",
                    }
                }
              />
            </MDBox>
          </Grid>
          {/* Sucursales */}
          <Grid item xs={12} md={6} lg={3}>
            <MDBox mb={1.5}>
              <ComplexStatisticsCard
                color="dark"
                icon="business"
                title="Sucursales"
                count={sucursales === null ? "Cargando..." : sucursales[0]}
                percentage={
                  sucursales === null
                    ? { color: "secondary", amount: "Cargando...", label: "" }
                    : {
                      color:
                        typeof sucursales[1] === "number" && sucursales[1] >= 0
                          ? "success"
                          : "error",
                      amount: formatPercentage(sucursales[1]),
                      label: "que ayer",
                    }
                }
              />
            </MDBox>
          </Grid>
          {/* Empleados */}
          <Grid item xs={12} md={6} lg={3}>
            <MDBox mb={1.5}>
              <ComplexStatisticsCard
                color="info"
                icon="groups"
                title="Empleados"
                count={count === null ? "Cargando..." : count}
                percentage={{
                  color: "success",
                  amount: "",
                  label: "Recién actualizado",
                }}
              />
            </MDBox>
          </Grid>
        </Grid>
        {/* Gráficas */}
        <MDBox mt={4.5}>
          <Grid container spacing={3}>
            <Grid item xs={12} md={6} lg={4}>
              <MDBox mb={3}>
                <ReportsBarChart
                  color="info"
                  title="Ingresos por Empresa"
                  description="Ingresos totales por empresa"
                  date="Recién actualizado"
                  chart={ingresosPorEmpresaData}
                />
              </MDBox>
            </Grid>
            <Grid item xs={12} md={6} lg={4}>
              <MDBox mb={3}>
                <ReportsLineChart
                  color="success"
                  title="Acciones"
                  description={<>Ventas de hoy</>}
                  date="Recién actualizado"
                  chart={accionesData.sales}
                />
              </MDBox>
            </Grid>
            <Grid item xs={12} md={6} lg={4}>
              <MDBox mb={3}>
                <ReportsLineChart
                  color="warning"
                  title="Ingresos Consolidados"
                  description="Ingresos totales generados por todas las empresas."
                  date="Recién actualizado"
                  chart={ingresosConsolidadosData.tasks}
                />
              </MDBox>
            </Grid>
          </Grid>
        </MDBox>
        {/* Proyectos y resumen de órdenes */}
        <MDBox>
          <Grid container spacing={3}>
            <Grid item xs={12} md={6} lg={8}>
              <Projects companyId={effectiveId} />
            </Grid>
            <Grid item xs={12} md={6} lg={4}>
              <OrdersOverview companyId={effectiveId} />
            </Grid>
          </Grid>
        </MDBox>
      </MDBox>
      <Footer />
    </DashboardLayout>
  );
}

export default Dashboard;
